import { useState } from "react";
import { LifeBuoy, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { reportLost } from "@/lib/api";
import { getDeviceId } from "@/lib/access";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const locate = () => new Promise((res) => {
  if (!navigator.geolocation) return res(null);
  navigator.geolocation.getCurrentPosition((p) => res([p.coords.latitude, p.coords.longitude]), () => res(null), { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 });
});

// "I'm lost" — sends the rider's location + route/stop to the shop so someone can come find them.
export const LostButton = ({ route, stopIndex, pos, className = "", compact = false }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const stop = route.stops[Math.min(stopIndex, route.stops.length - 1)];
  if (route.access && route.access.lost_enabled === false) return null;

  const send = async () => {
    setBusy(true);
    try {
      const loc = pos || (await locate());
      const r = await reportLost({
        route_id: route.id, route_title: route.title, stop_index: stopIndex, stop_name: stop?.name || "",
        lat: loc?.[0] ?? null, lng: loc?.[1] ?? null, device_id: getDeviceId(),
      });
      setSent(r);
    } catch { toast.error("Couldn't send — try again or call the shop"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="lost-btn"
        onClick={() => { setOpen(true); setSent(null); }}
        className={`inline-flex items-center justify-center gap-2 rounded-full border border-[#001A4D]/30 bg-[#F5F5F0] text-[#001A4D] font-medium hover:bg-white transition-colors ${compact ? "h-11 px-4 text-sm" : "h-12 px-6"} ${className}`}
      >
        <LifeBuoy className="w-4 h-4" /> I'm lost
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm bg-[#F5F5F0] text-[#0A0A0A]" data-testid="lost-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{sent ? "Help is on the way" : "Need a hand?"}</DialogTitle></DialogHeader>
          {sent ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle2 className="w-5 h-5 text-[#001A4D] shrink-0 mt-0.5" />
                <p>We've got your location and the Whangamatā Cycles crew have been alerted. <strong>Stay where you are</strong> — someone will come and find you and get you back on the route.</p>
              </div>
              <p className="text-xs text-black/50 font-mono" data-testid="lost-sent-meta">
                {sent.lat != null ? `Location shared · ${sent.lat.toFixed(5)}, ${sent.lng.toFixed(5)}` : "Location unavailable — we know your last stop"} · near {stop?.name}
              </p>
              <button data-testid="lost-close-btn" onClick={() => setOpen(false)} className="w-full h-11 rounded-full bg-[#001A4D] text-white font-medium">OK</button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-black/70">Tap below and we'll send your GPS location to the shop. A team member will ride out to meet you and guide you back onto the route.</p>
              <button data-testid="lost-send-btn" onClick={send} disabled={busy} className="w-full h-12 rounded-full bg-[#0A0A0A] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LifeBuoy className="w-4 h-4" />} Send my location to the shop
              </button>
              <button onClick={() => setOpen(false)} className="w-full h-10 text-sm text-black/50">Cancel</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
